import { useState, useCallback, useMemo, useEffect } from 'react';
import { useScreenOrientation } from '../hooks/useScreenOrientation';
import './OptimizedImage.css';

interface OptimizedImageProps {
  src: string; // Original image path (without extension)
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  lazy?: boolean;
  priority?: boolean;
  onLoad?: () => void;
  onError?: () => void;
  // New prop to enable orientation-aware image selection
  orientationAware?: boolean;
  // New prop to enable timeout fallback for missing images
  enableTimeout?: boolean;
}

/**
 * Optimized image component that serves WebP with JPEG fallback
 * Automatically handles lazy loading and responsive sizing for mobile performance
 * 
 * Features:
 * - WebP format with JPEG fallback for browser compatibility
 * - Lazy loading with loading placeholders
 * - Orientation-aware image selection (when orientationAware=true)
 * - Automatic fallback if orientation-specific images don't exist
 * - Responsive to screen rotation changes
 * 
 * Orientation-aware naming convention:
 * - Portrait: image-portrait.webp, image-portrait.jpg
 * - Landscape: image-landscape.webp, image-landscape.jpg
 * - Fallback: image.webp, image.jpg
 */
export default function OptimizedImage({
  src,
  alt,
  className = '',
  width,
  height,
  lazy = true,
  priority = false,
  onLoad,
  onError,
  orientationAware = false,
  enableTimeout = false
}: OptimizedImageProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  
  // Handle empty or null src
  if (!src || src.trim() === '') {
    return (
      <div className={`optimized-image-container no-image ${className}`}>
        <div className="optimized-image-placeholder no-cover">
          <div className="no-cover-icon">📚</div>
          <div className="no-cover-text">No Cover</div>
        </div>
      </div>
    );
  }
  
  // Get screen orientation for responsive image selection
  const { isPortrait } = useScreenOrientation();

  // Add timeout to handle cases where onError doesn't fire properly (only when enabled)
  useEffect(() => {
    // Reset states when src changes
    setImageError(false);
    setImageLoaded(false);
    
    if (enableTimeout) {
      const timeout = setTimeout(() => {
        if (!imageLoaded && !imageError) {
          console.log('Image load timeout, showing error fallback for:', src);
          setImageError(true);
        }
      }, 3000); // 3 second timeout

      return () => clearTimeout(timeout);
    }
  }, [src, enableTimeout]);

  // Generate orientation-aware image paths with fallback support
  const { webpSrc, jpegSrc, orientationSuffix } = useMemo(() => {
    // If src is a full URL (R2 asset), use it as-is for both webp and jpeg sources
    if (src.startsWith('http')) {
      return {
        webpSrc: src, // Use original URL as-is
        jpegSrc: src, // Use original URL as-is
        orientationSuffix: ''
      };
    }
    
    // Legacy path handling for static files
    const basePath = src.replace(/\.[^.]*$/, '');
    
    if (!orientationAware || useFallback) {
      // Original behavior - no orientation detection or fallback mode
      return {
        webpSrc: `${basePath}.webp`,
        jpegSrc: `${basePath}.jpg`,
        orientationSuffix: ''
      };
    }

    // Determine orientation suffix based on screen
    const suffix = isPortrait ? '-portrait' : '-landscape';
    
    return {
      webpSrc: `${basePath}${suffix}.webp`,
      jpegSrc: `${basePath}${suffix}.jpg`,
      orientationSuffix: suffix
    };
  }, [src, orientationAware, isPortrait, useFallback]);

  // Reset fallback state when orientation changes to try orientation-specific image again
  useEffect(() => {
    if (orientationAware && useFallback) {
      setUseFallback(false);
      setImageLoaded(false);
      setImageError(false);
    }
  }, [isPortrait, orientationAware, useFallback]);

  const handleLoad = useCallback(() => {
    setImageLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    if (orientationAware && !useFallback && orientationSuffix) {
      // Try fallback to non-orientation-specific image
      console.log(`Failed to load orientation-specific image${orientationSuffix}, trying fallback`);
      setUseFallback(true);
      setImageLoaded(false);
      return;
    }
    
    // Final error - no more fallbacks available
    setImageError(true);
    onError?.();
  }, [onError, orientationAware, useFallback, orientationSuffix]);

  const imageProps = {
    alt,
    className: `optimized-image ${className} ${imageLoaded ? 'loaded' : 'loading'}`,
    width,
    height,
    onLoad: handleLoad,
    onError: handleError,
    loading: (lazy && !priority) ? 'lazy' as const : 'eager' as const,
    decoding: 'async' as const
  };

  if (imageError) {
    return (
      <div className={`optimized-image-error ${className}`}>
        <div className="playing-graphic">
          <div className="playing-icon">
            <div className="sound-wave">
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
            </div>
          </div>
          <p className="playing-text">Playing...</p>
        </div>
      </div>
    );
  }

  return (
    <picture className="optimized-image-container">
      {/* WebP source for modern browsers */}
      <source srcSet={webpSrc} type="image/webp" />
      
      {/* JPEG fallback for older browsers */}
      <source srcSet={jpegSrc} type="image/jpeg" />
      
      {/* Fallback image element */}
      <img
        {...imageProps}
        src={jpegSrc}
      />
      
      {/* Loading placeholder */}
      {!imageLoaded && (
        <div className="optimized-image-placeholder">
          <div className="placeholder-shimmer"></div>
        </div>
      )}
    </picture>
  );
}